import { Delete } from '@mui/icons-material';
import {
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	IconButton,
	Typography,
} from '@mui/material';
import { FC, useEffect, useRef, useState } from 'react';
import { FileDrop } from 'react-file-drop';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import {
	getPublicAttachmentUploadInfoRequest,
	resetPublicAttachmentState,
	uploadPublicAttachmentRequest,
} from 'redux/reducers/publicAttachment/reducer';
import {
	getPublicAttachmentUploadFileLoading,
	getPublicAttachmentUploadInfo,
	getPublicAttachmentUploadInfoError,
	getPublicAttachmentUploadInfoLoading,
	getPublicAttachmentUploadSuccess,
} from 'redux/reducers/publicAttachment/selectors';
import { VisuallyHiddenInput } from 'ui/VisuallyHiddenInput';

export const PublicAttachmentUpload: FC = () => {
	const dispatch = useDispatch();
	const { hash } = useParams<{ hash: string }>();
	const uploadInfo = useSelector(getPublicAttachmentUploadInfo);
	const uploadInfoLoading = useSelector(getPublicAttachmentUploadInfoLoading);
	const uploadInfoError = useSelector(getPublicAttachmentUploadInfoError);
	const uploadFileLoading = useSelector(getPublicAttachmentUploadFileLoading);
	const uploadSuccess = useSelector(getPublicAttachmentUploadSuccess);

	const [file, setFile] = useState<File | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (hash) {
			dispatch(getPublicAttachmentUploadInfoRequest({ hash }));
		}

		return () => {
			dispatch(resetPublicAttachmentState());
		};
	}, [dispatch, hash]);

	const onTargetClick = () => {
		fileInputRef.current?.click();
	};

	const onDrop = (droppedFiles: FileList | null, event: React.DragEvent<HTMLDivElement>) => {
		if (droppedFiles && droppedFiles.length > 0) {
			setFile(droppedFiles[0]);
			event.preventDefault();
			event.stopPropagation();
		}
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			setFile(event.target.files[0]);
		}
	};

	const handleUpload = () => {
		if (!file || !hash) return;

		const formData = new FormData();
		formData.append('File', file, file.name);
		dispatch(uploadPublicAttachmentRequest({ hash, file: formData }));
	};

	if (uploadInfoLoading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
				<CircularProgress color="primary" />
			</Box>
		);
	}

	if (uploadInfoError) {
		return (
			<Card
				sx={{
					width: { xs: '100%', sm: 500 },
					maxWidth: '90vw',
					textAlign: 'center',
					color: '#016193',
				}}
			>
				<CardContent sx={{ p: 4 }}>
					<Typography variant="h5" component="h1" color="error" gutterBottom>
						Upload link is not valid
					</Typography>
					<Typography variant="body1" color="text.secondary">
						{uploadInfoError}
					</Typography>
				</CardContent>
			</Card>
		);
	}

	if (!uploadInfo) return null;

	return (
		<Card
			sx={{
				width: { xs: '100%', sm: 600 },
				maxWidth: '90vw',
				color: '#016193',
			}}
		>
			<CardContent sx={{ p: { xs: 2, sm: 4 } }}>
				<Typography variant="h5" component="h1" gutterBottom textAlign="center">
					Upload File
				</Typography>

				<Box sx={{ mb: 3, textAlign: 'center' }}>
					<Typography variant="body1" gutterBottom>
						<b>Merchant:</b> {uploadInfo.emFullName}
					</Typography>
					{uploadInfo.fileType && (
						<Typography variant="body1" gutterBottom>
							<b>File type:</b> {uploadInfo.fileType}
						</Typography>
					)}
					{uploadInfo.comment && (
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							{uploadInfo.comment}
						</Typography>
					)}
				</Box>

				{uploadSuccess ? (
					<Box sx={{ textAlign: 'center', py: 4 }}>
						<Typography variant="h6" color="success.main" gutterBottom>
							File uploaded successfully
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Thank you. Your file has been received.
						</Typography>
					</Box>
				) : (
					<>
						{!file && (
							<FileDrop onDrop={onDrop} onTargetClick={onTargetClick}>
								Drag file here or click to browse
								<div style={{ fontSize: '.8rem', marginTop: '15px' }}>
									<b>File size should not exceed 10 Mb.</b>
								</div>
							</FileDrop>
						)}

						{file && (
							<Box>
								<Box
									sx={{
										fontSize: '1.2rem',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										color: 'primary.main',
										mt: 2,
									}}
								>
									<Box color={file.size > 10_000_000 ? 'error.main' : 'auto'}>
										<b>{file.name}</b>,{' '}
										{file.size > 1_000_000 ? (
											<span>{(file.size / 1_000_000).toFixed(3)} MB</span>
										) : file.size > 1000 ? (
											<span>{(file.size / 1000).toFixed(3)} KB</span>
										) : (
											<span>{file.size} B</span>
										)}
									</Box>
									<IconButton
										onClick={() => setFile(null)}
										color="primary"
										disabled={uploadFileLoading}
									>
										<Delete />
									</IconButton>
								</Box>
								{file.size > 10_000_000 && (
									<Typography variant="body2" color="error" sx={{ mt: 1 }}>
										File size exceeds 10 Mb limit
									</Typography>
								)}
							</Box>
						)}

						<VisuallyHiddenInput ref={fileInputRef} type="file" onChange={handleFileChange} />

						<Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
							<Button
								variant="contained"
								onClick={handleUpload}
								disabled={!file || file.size > 10_000_000 || uploadFileLoading}
							>
								{uploadFileLoading ? <CircularProgress size={24} color="inherit" /> : 'Upload'}
							</Button>
						</Box>
					</>
				)}
			</CardContent>
		</Card>
	);
};
